import React, { useState } from 'react';
import { ContactInfo } from '../../../services/api';

interface ContactInfoEditorProps {
  initialContactInfo: ContactInfo;
  onSave: (contactInfo: ContactInfo) => Promise<{ success: boolean; error?: string }>;
}

const ContactInfoEditor: React.FC<ContactInfoEditorProps> = ({ initialContactInfo, onSave }) => {
  const [contactInfo, setContactInfo] = useState<ContactInfo>(initialContactInfo);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    
    try {
      // Validate email
      if (!contactInfo.email || !isValidEmail(contactInfo.email)) {
        setError('Please enter a valid email address');
        setSaving(false);
        return;
      }
      
      const result = await onSave(contactInfo);
      
      if (result.success) {
        setSuccess(true);
        setIsEditing(false);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError(result.error || 'Failed to save contact information');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setContactInfo(initialContactInfo);
    setIsEditing(false);
    setError(null);
  };

  const handleChange = (field: keyof ContactInfo, value: string) => {
    setContactInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-medium text-gray-800">Contact Information</h3>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
          >
            Edit Contact
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                value={contactInfo.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="your@email.com"
                required
              />
            </div>
            
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                id="phone"
                type="tel"
                value={contactInfo.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="+1 (555) 123-4567"
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
              Public Address (optional)
            </label>
            <textarea
              id="address"
              value={contactInfo.address || ''}
              onChange={(e) => handleChange('address', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Enter the address you're comfortable sharing publicly..."
              rows={3}
            />
            <p className="text-sm text-gray-500 mt-1">
              This address will be visible on your public profile. Only include information you're comfortable sharing.
            </p>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Contact Info'}
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
          
          {error && (
            <div className="text-red-500 text-sm">
              {error}
            </div>
          )}
          
          {success && (
            <div className="text-green-500 text-sm">
              Contact information saved successfully!
            </div>
          )}
        </div>
      ) : (
        <div className="bg-gray-50 p-4 rounded-md">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">Email</dt>
              <dd className="mt-1 text-gray-900">{contactInfo.email || 'Not set'}</dd>
            </div>
            
            <div>
              <dt className="text-sm font-medium text-gray-500">Phone</dt>
              <dd className="mt-1 text-gray-900">{contactInfo.phone || 'Not set'}</dd>
            </div>
            
            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">Address</dt>
              <dd className="mt-1 text-gray-900">{contactInfo.address || 'Not set'}</dd>
            </div>
          </dl>
        </div>
      )}
    </div>
  );
};

export default ContactInfoEditor;